/** Compuertas del wizard de setup (feedback de Eduardo 2026-07-14): el admin
 * NO avanza de fase hasta completar la actual en TODAS las categorías.
 *
 * - Paso 5 Formato: toda categoría necesita formato guardado.
 * - Paso 6 Ranking: NO bloquea — "sin rank" (S/R) es un estado válido
 *   (feedback 2026-07-15); el draw acomoda a los S/R en orden aleatorio.
 * - Paso 7 Grupos: toda categoría de round robin con inscritos necesita a
 *   todos sus jugadores en un grupo.
 * - Paso 8 Draw: toda categoría con inscritos necesita draw generado.
 *
 * `maxStep` = el paso más lejano alcanzable; los chips posteriores se
 * bloquean y se muestra el motivo en rojo.
 */

export interface SetupGateRow {
  division_id: number | null;
  name: string;
  format_saved: boolean;
  participants: number;
  has_draw: boolean;
  unranked?: number;
  needs_groups?: boolean;
  ungrouped?: number;
}

export interface SetupGateResult {
  maxStep: number;
  reason: string | null;
}

function names(rows: SetupGateRow[]): string {
  return rows.map((row) => row.name).join(", ");
}

// Tope "todo abierto" del wizard. El paso 10 Calendario es opcional y nunca
// genera razón de bloqueo propia — solo existe como destino navegable.
export const SETUP_MAX_STEP = 10;

export function setupGate(rows: SetupGateRow[]): SetupGateResult {
  if (rows.length === 0) return { maxStep: SETUP_MAX_STEP, reason: null };

  const sinFormato = rows.filter((row) => !row.format_saved);
  if (sinFormato.length > 0) {
    return {
      maxStep: 6,
      reason: `No puedes avanzar: define el formato de todas las categorías. Falta: ${names(sinFormato)}.`,
    };
  }

  const conJugadores = rows.filter((row) => row.participants > 0);
  const sinGrupos = conJugadores.filter(
    (row) => Boolean(row.needs_groups) && (row.ungrouped ?? 0) > 0,
  );
  if (sinGrupos.length > 0) {
    return {
      maxStep: 8,
      reason: `No puedes avanzar: asigna todos los jugadores a un grupo. Falta en: ${names(sinGrupos)}.`,
    };
  }

  const sinDraw = conJugadores.filter((row) => !row.has_draw);
  if (sinDraw.length > 0) {
    return {
      maxStep: 9,
      reason: `No puedes avanzar: genera el draw de todas las categorías. Falta: ${names(sinDraw)}.`,
    };
  }

  return { maxStep: SETUP_MAX_STEP, reason: null };
}

export type SetupReadinessState = "loading" | "loaded" | "failed";

export function canAdvanceFromSetupStep(
  rows: SetupGateRow[],
  currentStep: number,
  readinessState: SetupReadinessState = "loaded",
): boolean {
  if (readinessState === "loading" || readinessState === "failed") return false;
  return setupGate(rows).maxStep > currentStep;
}

export function canNavigateToSetupStep(
  targetStep: number,
  currentStep: number,
  gate: SetupGateResult,
  readinessState: SetupReadinessState,
): boolean {
  if (targetStep <= currentStep) return true;
  if (readinessState !== "loaded") return false;
  return targetStep <= gate.maxStep;
}
